import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import TrackingModal from '../../components/customer-portal/TrackingModal';
import DocketDetailModal from '../../components/customer-portal/DocketDetailModal';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Truck, Package, CheckCircle2, Clock, PlusCircle, 
  FileText, ArrowRight, Eye, Phone, MessageSquare, Loader2, 
  TrendingUp, ShieldCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tracking & Detail Modals
  const [selectedDocketId, setSelectedDocketId] = useState(null);
  const [selectedDocketNo, setSelectedDocketNo] = useState('');
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    if (!token) {
      navigate('/customer-login');
      return;
    }
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API}/api/client-portal/dashboard`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          navigate('/customer-login');
          return;
        }
        throw new Error('Failed to load dashboard data');
      }
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTracking = (id, no) => {
    setSelectedDocketId(id);
    setSelectedDocketNo(no);
    setIsTrackingOpen(true);
  };

  const handleOpenDetail = (id) => {
    setSelectedDocketId(id);
    setIsDetailOpen(true);
  };

  return (
    <CustomerPortalLayout>
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-lg mb-8 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-semibold mb-2 border border-blue-400/20">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Verified Client Portal</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold font-manrope">
              Welcome back, {stats?.customer_name || 'Valued Client'}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 mt-1 max-w-xl">
              Monitor active shipments, track consignment milestones, request new bookings, and manage quotations.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/customer-portal/request-booking')}
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-xs h-11 px-5 shadow-md shadow-blue-600/30 flex items-center gap-2"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Request a Booking</span>
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Total Shipments */}
        <Card 
          onClick={() => navigate('/customer-portal/dockets')}
          className="p-5 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer bg-white"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Dockets</span>
            <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center">
              <Package className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl sm:text-3xl font-bold font-manrope text-slate-900">
              {loading ? '...' : (stats?.total_dockets ?? 0)}
            </span>
            <span className="text-[11px] text-slate-500 block mt-0.5">Historical consignments</span>
          </div>
        </Card>

        {/* Active / In Transit */}
        <Card 
          onClick={() => navigate('/customer-portal/dockets?status=ACTIVE')}
          className="p-5 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer bg-white"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Active In-Transit</span>
            <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
              <Truck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl sm:text-3xl font-bold font-manrope text-blue-600">
              {loading ? '...' : (stats?.active_shipments ?? 0)}
            </span>
            <span className="text-[11px] text-slate-500 block mt-0.5">Live on the road / air</span>
          </div>
        </Card>

        {/* Delivered This Month */}
        <Card 
          onClick={() => navigate('/customer-portal/dockets?status=DELIVERED')}
          className="p-5 hover:shadow-md hover:border-emerald-200 transition-all cursor-pointer bg-white"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Delivered (Month)</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl sm:text-3xl font-bold font-manrope text-emerald-600">
              {loading ? '...' : (stats?.delivered_this_month ?? 0)}
            </span>
            <span className="text-[11px] text-slate-500 block mt-0.5">e-PODs ready for download</span>
          </div>
        </Card>

        {/* Pending Booking Requests */}
        <Card 
          onClick={() => navigate('/customer-portal/booking-requests')}
          className="p-5 hover:shadow-md hover:border-amber-200 transition-all cursor-pointer bg-white"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Booking Requests</span>
            <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl sm:text-3xl font-bold font-manrope text-amber-600">
              {loading ? '...' : (stats?.pending_bookings ?? 0)}
            </span>
            <span className="text-[11px] text-slate-500 block mt-0.5">Under review by ops desk</span>
          </div>
        </Card>
      </div>

      {/* Two-Column Grid: Recent Shipments + Dedicated Support */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Recent Shipments Feed */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold font-manrope text-slate-900">Recent Shipments</h2>
              <p className="text-xs text-slate-500">Latest consignment movements linked to your account</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/customer-portal/dockets')}
              className="text-xs font-bold text-blue-600 hover:text-blue-700 border-slate-200"
            >
              <span>View All Shipments</span>
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>

          <Card className="bg-white rounded-xl border border-slate-200/80 overflow-hidden shadow-xs">
            {loading ? (
              <div className="py-12 flex flex-col items-center justify-center text-slate-400 gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="text-xs">Loading shipment feed...</span>
              </div>
            ) : !stats?.recent_dockets || stats.recent_dockets.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center text-center p-6 text-slate-400">
                <Package className="w-10 h-10 text-slate-300 mb-2 stroke-1" />
                <p className="text-sm font-semibold text-slate-700">No shipments found</p>
                <p className="text-xs text-slate-400 mt-1 max-w-xs">
                  Your consignments will appear here once bookings are confirmed and dispatched.
                </p>
                <Button
                  onClick={() => navigate('/customer-portal/request-booking')}
                  className="mt-4 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs h-9 px-4 rounded-xl"
                >
                  <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
                  Request First Booking
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold text-[11px]">
                    <tr>
                      <th className="px-5 py-3.5">Docket No</th>
                      <th className="px-5 py-3.5">Route</th>
                      <th className="px-5 py-3.5">Mode</th>
                      <th className="px-5 py-3.5">Status</th>
                      <th className="px-5 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {stats.recent_dockets.map((d) => (
                      <tr key={d.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-5 py-3.5 font-bold font-mono text-slate-900">
                          {d.docket_number}
                          {d.booking_date && (
                            <span className="block text-[10px] text-slate-400 font-normal font-ibm mt-0.5">
                              {d.booking_date}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5 text-slate-800 font-medium">
                            <span className="truncate max-w-[120px]">{d.origin}</span>
                            <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="truncate max-w-[120px]">{d.destination}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 font-semibold text-slate-600">
                          {d.shipment_type || 'ROAD'}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            d.status === 'DELIVERED' || d.status === 'COMPLETED'
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : d.status === 'IN_TRANSIT'
                              ? 'bg-blue-100 text-blue-800 border border-blue-200'
                              : 'bg-amber-100 text-amber-800 border border-amber-200'
                          }`}>
                            {d.status?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenTracking(d.id, d.docket_number)}
                            className="h-8 text-xs font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2.5 rounded-lg"
                          >
                            <Truck className="w-3.5 h-3.5 mr-1" />
                            Track
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenDetail(d.id)}
                            className="h-8 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2.5 rounded-lg"
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* Right Col: Quick Support & Estimates Shortcut */}
        <div className="space-y-4">
          {/* Quick Booking Hero Widget */}
          <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl p-5 shadow-sm">
            <h3 className="text-base font-bold font-manrope">Ship with LogisticsHub</h3>
            <p className="text-xs text-blue-100 mt-1 leading-relaxed">
              Book domestic road freight or international air/ocean shipments in a few clicks.
            </p>
            <Button
              onClick={() => navigate('/customer-portal/request-booking')}
              className="mt-4 w-full bg-white hover:bg-blue-50 text-blue-900 font-bold rounded-xl text-xs h-10 shadow-sm"
            >
              New Booking Request
            </Button>
          </Card>

          {/* Account Manager / Support Card */}
          <Card className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block">
                  Dedicated Account Desk
                </span>
                <h4 className="text-sm font-bold text-slate-900 font-manrope">Operations &amp; Support</h4>
              </div>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed">
              Need rate negotiation, emergency shipment rerouting, or custom documentation assistance?
            </p>

            <a
              href="https://wa.me/919876543210?text=Hello%20LogisticsHub%20Support%2C%20I%20need%20assistance%20with%20my%20shipments"
              target="_blank"
              rel="noreferrer"
              className="w-full py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-colors shadow-xs"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Priority WhatsApp Desk</span>
            </a>
          </Card>
        </div>
      </div>

      {/* Modals */}
      <TrackingModal
        isOpen={isTrackingOpen}
        onClose={() => setIsTrackingOpen(false)}
        docketId={selectedDocketId}
        docketNumber={selectedDocketNo}
      />
      <DocketDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        docketId={selectedDocketId}
        onTrackClick={(id, no) => handleOpenTracking(id, no)}
      />
    </CustomerPortalLayout>
  );
}
